import type { FC } from 'react';
import { useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DevHubBox } from '@devxdiscover/devhub-ui';
import {
  createApplicationNameTemplate,
  createModeTemplate,
  createCheckboxTemplate,
  createSelectAllFilterTemplate,
  createResetTemplate,
} from '../utils/dataTableTemplates';

export interface ApplicationsTableProps {
  // Data
  matrixTableRows: any[];
  functionalAreaDataLeads: any[];
  selectedFunctionalArea: string;
  activeFunctionalAreaApps: any[];

  // State
  globalFilterValue: string;
  currentPageSize: number;
  selectedAppsForUpdate: Set<number>;
  isRefreshing: boolean;

  // Handlers
  onPage: (e: any) => void;
  onSelectionChange: (e: any) => void;

  // Template props for cell renderers
  templateProps: any;

  // Data lead manager for application modes
  dataLeadManager: {
    applicationModes: any;
  };

  // Header component
  header: () => React.ReactNode;
}

/**
 * ApplicationsTable component for displaying the applications table
 * @param param0 - Props for the component
 * @returns ApplicationsTable JSX element
 */
export const ApplicationsTable: FC<ApplicationsTableProps> = ({
  matrixTableRows,
  functionalAreaDataLeads,
  selectedFunctionalArea,
  activeFunctionalAreaApps,
  globalFilterValue,
  currentPageSize,
  selectedAppsForUpdate,
  isRefreshing,
  onPage,
  onSelectionChange,
  templateProps,
  dataLeadManager,
  header,
}) => {
  // Load Material Design theme based on DevHub theme setting
  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  return (
    <DevHubBox>
      <DevHubBox style={{ width: '100%', overflowX: 'auto' }}>
        <style>{`
          .no-row-highlight .p-datatable-tbody > tr.p-highlight {
            background: transparent !important;
          }

          .p-datatable {
            border: none !important;
          }
          .p-datatable .p-datatable-header {
            border: none !important;
            padding: 0 !important;
            background: transparent !important;
          }
          .p-datatable .p-datatable-thead > tr > th:not(:nth-child(1)):not(:nth-child(2)):not(:nth-child(3)) {
            text-align: center !important;
            padding: 0.5rem !important;
          }
          .p-datatable .p-datatable-thead > tr > th:not(:nth-child(1)):not(:nth-child(2)):not(:nth-child(3)) > div {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 4px !important;
            width: 100% !important;
            margin: 0 auto !important;
          }
          .p-datatable .p-datatable-tbody > tr > td:not(:nth-child(1)):not(:nth-child(2)):not(:nth-child(3)) {
            text-align: center !important;
            padding: 0.5rem !important;
          }
        `}</style>
        <DataTable
          className="p-datatable-sm no-row-highlight"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} applications"
          emptyMessage="No applications found for the selected functional area"
          globalFilter={globalFilterValue}
          globalFilterFields={['name', 'applicationId']}
          header={header}
          key={`matrixgrid-${selectedFunctionalArea}-${activeFunctionalAreaApps.length}`}
          loading={isRefreshing}
          onPage={onPage}
          onSelectionChange={onSelectionChange}
          paginator
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          rows={currentPageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          selection={matrixTableRows.filter(row =>
            selectedAppsForUpdate.has(row.applicationId),
          )}
          selectionMode="checkbox"
          size="small"
          sortField="name"
          sortOrder={1}
          style={{ fontSize: '0.9rem' }}
          value={matrixTableRows}
        >
          <Column
            selectionMode="multiple"
            header={
              <span
                style={{
                  position: 'absolute',
                  left: '-10000px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                Select All
              </span>
            }
            headerStyle={{ width: '3rem' }}
          />
          <Column
            field="applicationId"
            header="App ID"
            sortable
            style={{
              width: '90px',
              minWidth: '90px',
              maxWidth: '90px',
              textAlign: 'left',
            }}
            headerStyle={{ textAlign: 'center' }}
          />
          <Column
            field="name"
            header="Application Name"
            body={createApplicationNameTemplate(templateProps)()}
            sortable
            style={{
              minWidth: '250px',
              position: 'sticky',
              left: '6.25rem',
              backgroundColor: 'white',
              zIndex: 1,
            }}
            frozen
          />
          <Column
            header="Data Leads"
            body={createModeTemplate(dataLeadManager.applicationModes)}
            style={{
              minWidth: '90px',
              textAlign: 'center',
              position: 'sticky',
              left: '340px',
              backgroundColor: 'white',
              zIndex: 1,
            }}
            headerStyle={{
              textAlign: 'center',
              fontSize: '0.9rem',
              padding: '0.5rem',
            }}
            frozen
          />
          {functionalAreaDataLeads.map((lead: any) => (
            <Column
              key={lead.login}
              field={`lead_${lead.login}`}
              header={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  <span style={{ textAlign: 'center', width: '100%' }}>
                    {lead.fullName}
                  </span>
                  <span
                    style={{
                      textAlign: 'center',
                      width: '100%',
                      fontSize: '0.75rem',
                      fontWeight: 300,
                      color: '#666',
                      marginTop: '2px',
                    }}
                  >
                    {lead.login}
                  </span>
                  {createSelectAllFilterTemplate(templateProps)(
                    lead.login,
                    matrixTableRows,
                  )()}
                </div>
              }
              body={createCheckboxTemplate(templateProps)(lead.login)}
              style={{ minWidth: '120px', textAlign: 'center' }}
              headerStyle={{
                textAlign: 'center',
                fontSize: '0.9rem',
                padding: '0.5rem',
              }}
            />
          ))}
          <Column
            header="Reset"
            body={createResetTemplate(templateProps)()}
            style={{ width: '80px', textAlign: 'center' }}
            headerStyle={{
              textAlign: 'center',
              fontSize: '0.9rem',
              padding: '0.5rem',
            }}
          />
        </DataTable>
      </DevHubBox>
    </DevHubBox>
  );
};
